import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Avatar, Chip, InputBase, Switch, IconButton, Menu, MenuItem,
  CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, ListItemIcon, Pagination,
  Collapse, Tooltip
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';

import adminAxios from '../../utils/adminAxios';
import { urlImg } from '../../components/common/helpers';

const TABS = [
  { key: 'negocios', label: 'Grupos o Negocios', to: '/admin/negocios', icon: <ApartmentOutlinedIcon fontSize="small" /> },
  { key: 'paginas', label: 'Páginas de Servicios', to: '/admin/paginas', icon: <LayersOutlinedIcon fontSize="small" /> },
  { key: 'servicios', label: 'Servicios', to: '/admin/servicios', icon: <BoltOutlinedIcon fontSize="small" /> },
];

const tabDeRuta = (pathname) => {
  if (pathname.includes('/paginas')) return 'paginas';
  if (pathname.includes('/servicios')) return 'servicios';
  return 'negocios';
};

const Metrica = ({ valor, label }) => (
  <Box sx={{ textAlign: 'center', minWidth: 64 }}>
    <Typography sx={{ fontWeight: 700, color: 'primary.main' }}>{valor}</Typography>
    <Typography variant="caption" sx={{ color: '#94A3B8' }}>{label}</Typography>
  </Box>
);

// status de la membresía: 0 = procesando (pendiente), 1 = aceptado (activo), 2 = rechazado
const MEMBRESIA = {
  0: { label: 'Pendiente', bg: '#FFF1D6', color: '#A8740F' },
  1: { label: 'Activo', bg: '#D6F3E8', color: '#1B7A5A' },
  2: { label: 'Rechazado', bg: '#FFE0DD', color: '#B23B33' },
};

// Panel expandible con los facilitadores (miembros) de una página/negocio.
const Facilitadores = ({ pageId, onVer }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let vivo = true;
    adminAxios.get(`/api/admin/pages/${pageId}/facilitadores`)
      .then(res => { if (vivo) setData(res.data.facilitadores); })
      .catch(() => { if (vivo) setError(true); });
    return () => { vivo = false; };
  }, [pageId]);

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #F1F5F9' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, color: 'secondary.main' }}>
        <GroupOutlinedIcon fontSize="small" />
        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
          Facilitadores asignados{data ? ` (${data.length})` : ''}
        </Typography>
      </Box>

      {error ? (
        <Typography variant="caption" sx={{ color: '#B23B33' }}>No se pudieron cargar los facilitadores.</Typography>
      ) : !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} /></Box>
      ) : data.length === 0 ? (
        <Typography variant="caption" sx={{ color: '#94A3B8' }}>Esta entidad no tiene facilitadores asignados.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {data.map((f) => {
            const m = MEMBRESIA[f.membresia] || MEMBRESIA[0];
            return (
              <Box key={f._id}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, py: 1.2, px: 1, borderRadius: 2,
                  '&:hover': { bgcolor: '#F8FAFC' },
                }}>
                <Avatar src={f.avatar ? `${urlImg}/users/${f._id}/${f.avatar}` : undefined}
                  sx={{ width: 36, height: 36, bgcolor: '#EEF2FF', color: 'secondary.main' }}>
                  <PersonOutlineOutlinedIcon fontSize="small" />
                </Avatar>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontWeight: 600, fontSize: 14, color: 'primary.main' }}>{f.nombre}</Typography>
                  {f.email && <Typography noWrap variant="caption" sx={{ color: '#94A3B8' }}>{f.email}</Typography>}
                </Box>
                <Chip label={m.label} size="small" sx={{ height: 22, fontWeight: 700, bgcolor: m.bg, color: m.color }} />
                <Tooltip title="Ver perfil">
                  <span>
                    <IconButton size="small" onClick={() => onVer(f._id)}>
                      <VisibilityOutlinedIcon fontSize="small" sx={{ color: '#94A3B8' }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const Directorio = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const tab = tabDeRuta(location.pathname);

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ negocios: 0, paginas: 0, servicios: 0 });
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [toast, setToast] = useState(null);

  const LIMIT = 15;

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItem, setMenuItem] = useState(null);
  const [confirmBaja, setConfirmBaja] = useState(null);
  const [expandido, setExpandido] = useState(null);

  const toggleExpandir = (id) => setExpandido((prev) => (prev === id ? null : id));
  const verPerfil = (userId) => window.open(`/perfil/${userId}`, '_blank');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await adminAxios.get('/api/admin/directorio', { params: { tab, q, page, limit: LIMIT } });
      setItems(res.data.items);
      setCounts(res.data.counts);
      setTotal(res.data.total);
    } catch (e) {
      console.error('Error directorio', e.message);
    } finally {
      setCargando(false);
    }
  }, [tab, q, page]);

  // Volver a la página 1 y cerrar expandidos al cambiar de tab, búsqueda o página.
  useEffect(() => { setPage(1); }, [tab, q]);
  useEffect(() => { setExpandido(null); }, [tab, q, page]);

  useEffect(() => {
    const t = setTimeout(cargar, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, q]);

  const totalPaginas = Math.ceil(total / LIMIT);

  const endpointDe = (it) => (it.tipo === 'page' ? 'pages' : 'services');

  const toggleStatus = async (it) => {
    const nuevo = !it.status;
    setItems((prev) => prev.map((x) => (x._id === it._id ? { ...x, status: nuevo } : x)));
    try {
      await adminAxios.patch(`/api/admin/${endpointDe(it)}/${it._id}/status`, { status: nuevo });
    } catch (e) {
      setItems((prev) => prev.map((x) => (x._id === it._id ? { ...x, status: it.status } : x)));
      setToast({ tipo: 'error', msg: 'No se pudo cambiar el estado.' });
    }
  };

  const darDeBaja = async () => {
    const it = confirmBaja;
    setConfirmBaja(null);
    try {
      await adminAxios.delete(`/api/admin/${endpointDe(it)}/${it._id}`);
      setToast({ tipo: 'success', msg: 'Dado de baja correctamente.' });
      cargar();
    } catch (e) {
      setToast({ tipo: 'error', msg: 'No se pudo dar de baja.' });
    }
  };

  const abrirMenu = (e, it) => { setMenuAnchor(e.currentTarget); setMenuItem(it); };
  const cerrarMenu = () => { setMenuAnchor(null); setMenuItem(null); };

  const iconoTab = TABS.find((t) => t.key === tab)?.icon;

  return (
    <Box>
      {/* Tabs + búsqueda */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {TABS.map((t) => {
            const activo = t.key === tab;
            return (
              <Box key={t.key} onClick={() => navigate(t.to)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1.2, borderRadius: 3, cursor: 'pointer',
                  bgcolor: activo ? 'secondary.main' : 'white', color: activo ? 'white' : '#475569',
                  border: '1px solid', borderColor: activo ? 'secondary.main' : '#F1F5F9', fontWeight: 600, transition: 'all .15s',
                }}>
                {t.icon}
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{t.label}</Typography>
                <Chip label={counts[t.key]} size="small"
                  sx={{ height: 20, minWidth: 24, bgcolor: activo ? 'rgba(255,255,255,0.25)' : '#F1F5F9', color: activo ? 'white' : '#737791', fontWeight: 700 }} />
              </Box>
            );
          })}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'white', border: '1px solid #F1F5F9', borderRadius: 3, px: 2, py: 1, minWidth: 240 }}>
          <SearchOutlinedIcon sx={{ color: '#94A3B8' }} fontSize="small" />
          <InputBase placeholder="Buscar negocio, servicio..." value={q} onChange={(e) => setQ(e.target.value)} sx={{ fontSize: 14, flex: 1 }} />
        </Box>
      </Box>

      {/* Lista */}
      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Paper sx={{ p: 6, borderRadius: 3, textAlign: 'center', color: '#737791' }}>No hay entidades en esta categoría.</Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {items.map((it) => {
            // Solo en "Grupos o Negocios": en páginas de servicios el único miembro es el propio creador.
            const expandible = tab === 'negocios' && it.tipo === 'page' && it.facilitadores > 0;
            const abierto = expandido === it._id;
            return (
            <Paper key={it._id} sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Tooltip title={expandible ? (abierto ? 'Ocultar facilitadores' : 'Ver facilitadores') : ''}>
                  <span>
                    <IconButton size="small" disabled={!expandible} onClick={() => toggleExpandir(it._id)}
                      sx={{
                        bgcolor: '#F1F5F9', width: 32, height: 32, opacity: expandible ? 1 : 0,
                        transition: 'transform .2s', transform: abierto ? 'rotate(180deg)' : 'none',
                      }}>
                      <ExpandMoreRoundedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Avatar variant="rounded" sx={{ bgcolor: '#EEF2FF', color: 'secondary.main', width: 48, height: 48 }}>{iconoTab}</Avatar>

                <Box sx={{ flexGrow: 1, minWidth: 180 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 700, color: 'primary.main' }}>{it.nombre}</Typography>
                    <Chip label={it.status ? 'Activo' : 'Inactivo'} size="small"
                      sx={{ height: 20, fontWeight: 700, bgcolor: it.status ? '#D6F3E8' : '#FFE0DD', color: it.status ? '#1B7A5A' : '#B23B33' }} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#737791', mt: 0.3 }}>
                    <Typography variant="caption">{it.propietario || 'Sin propietario'}</Typography>
                    {it.ubicacion && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <LocationOnOutlinedIcon sx={{ fontSize: 14 }} />
                        <Typography variant="caption">{it.ubicacion}</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Métricas */}
                {it.tipo === 'page' ? (
                  <>
                    <Metrica valor={it.facilitadores} label="Facilitadores" />
                    <Metrica valor={it.servicios} label="Servicios" />
                  </>
                ) : (
                  <Box sx={{ minWidth: 120 }}>
                    <Chip label={it.categoria || 'Sin categoría'} size="small" variant="outlined" sx={{ borderColor: '#E2E8F0' }} />
                  </Box>
                )}

                {/* Rating */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, minWidth: 56 }}>
                  <StarRoundedIcon sx={{ color: it.rating > 0 ? '#FFB400' : '#CBD5E1', fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 700, color: it.rating > 0 ? 'primary.main' : '#94A3B8' }}>
                    {it.rating > 0 ? it.rating.toFixed(1) : '—'}
                  </Typography>
                </Box>

                {/* Toggle estado */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 96 }}>
                  <Switch checked={!!it.status} onChange={() => toggleStatus(it)} color="success" size="small" />
                  <Typography variant="caption" sx={{ color: '#737791' }}>{it.status ? 'Activo' : 'Inactivo'}</Typography>
                </Box>

                <IconButton onClick={(e) => abrirMenu(e, it)}><MoreHorizOutlinedIcon /></IconButton>
              </Box>

              {expandible && (
                <Collapse in={abierto} timeout="auto" unmountOnExit>
                  <Facilitadores pageId={it._id} onVer={verPerfil} />
                </Collapse>
              )}
            </Paper>
          );
          })}
        </Box>
      )}

      {/* Paginador */}
      {!cargando && total > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, mt: 4 }}>
          {totalPaginas > 1 && (
            <Pagination
              count={totalPaginas}
              page={page}
              onChange={(e, v) => setPage(v)}
              color="primary"
              shape="rounded"
              size="large"
              sx={{ '& .MuiPaginationItem-root': { fontSize: 16, minWidth: 40, height: 40 } }}
            />
          )}
          <Typography variant="body2" sx={{ color: '#737791' }}>
            Mostrando {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} de {total}
          </Typography>
        </Box>
      )}

      {/* Menú de acciones */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={cerrarMenu}>
        <MenuItem onClick={() => { toggleStatus(menuItem); cerrarMenu(); }}>
          <ListItemIcon>{menuItem?.status ? <BlockOutlinedIcon fontSize="small" /> : <CheckCircleOutlineOutlinedIcon fontSize="small" />}</ListItemIcon>
          {menuItem?.status ? 'Desactivar' : 'Activar'}
        </MenuItem>
        <MenuItem onClick={() => { setConfirmBaja(menuItem); cerrarMenu(); }} sx={{ color: '#B23B33' }}>
          <ListItemIcon><DeleteOutlineOutlinedIcon fontSize="small" sx={{ color: '#B23B33' }} /></ListItemIcon>
          Dar de baja
        </MenuItem>
      </Menu>

      {/* Confirmar baja */}
      <Dialog open={!!confirmBaja} onClose={() => setConfirmBaja(null)}>
        <DialogTitle>Dar de baja</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que quieres dar de baja <strong>{confirmBaja?.nombre}</strong>? Quedará oculto en la plataforma (baja lógica, reversible).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBaja(null)}>Cancelar</Button>
          <Button onClick={darDeBaja} variant="contained" color="error">Dar de baja</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast && <Alert severity={toast.tipo} onClose={() => setToast(null)}>{toast.msg}</Alert>}
      </Snackbar>
    </Box>
  );
};

export default Directorio;
